// City-specific language support system - All 40 supported languages
export type CityLanguage = 
  // Original 6 languages
  'en' | 'es' | 'fr' | 'de' | 'it' | 'pt' | 
  // Nordic languages
  'da' | 'no' | 'fi' | 'sv' | 'is' |
  // Central European languages
  'cs' | 'sk' | 'sl' | 'pl' |
  // Baltic languages
  'et' | 'lv' | 'lt' |
  // Other European languages
  'el' | 'nl' | 'ro' | 'bg' | 'hr' | 'hu' | 'uk' | 'he' |
  // Asian developed countries
  'ko' | 'ja' | 'zh' |
  // Other major languages
  'ar' | 'hi' | 'ru' | 'tr' | 'id' |
  // Additional Asian languages
  'th' | 'vi' | 'ms' | 'ur' | 'bn' | 'fa' | 'tl' | 'sw';

export const cityLanguageNames: Record<CityLanguage, string> = {
  // Original 6
  'en': 'English',
  'es': 'Español',
  'fr': 'Français',
  'de': 'Deutsch',
  'it': 'Italiano',
  'pt': 'Português',
  // Nordic
  'da': 'Dansk',
  'no': 'Norsk',
  'fi': 'Suomi',
  'sv': 'Svenska',
  'is': 'Íslenska',
  // Central European
  'cs': 'Čeština',
  'sk': 'Slovenčina',
  'sl': 'Slovenščina',
  'pl': 'Polski',
  // Baltic
  'et': 'Eesti',
  'lv': 'Latviešu',
  'lt': 'Lietuvių',
  // Other European
  'el': 'Ελληνικά',
  'nl': 'Nederlands',
  // Asian
  'ko': '한국어',
  'ja': '日本語',
  'zh': '中文',
  // Other major
  'ar': 'العربية',
  'hi': 'हिन्दी',
  'ru': 'Русский',
  'tr': 'Türkçe',
  'id': 'Bahasa Indonesia',
  // Additional Asian
  'th': 'ไทย',
  'vi': 'Tiếng Việt',
  'ms': 'Bahasa Melayu',
  'ur': 'اردو',
  'bn': 'বাংলা',
  'fa': 'فارسی',
  'tl': 'Tagalog',
  // Additional European
  'ro': 'Română',
  'bg': 'Български',
  'hr': 'Hrvatski',
  'hu': 'Magyar',
  'uk': 'Українська',
  'he': 'עברית',
  'sw': 'Kiswahili'
};

export const cityLanguageFlags: Record<CityLanguage, string> = {
  // Original 6
  'en': '🇬🇧',
  'es': '🇪🇸',
  'fr': '🇫🇷',
  'de': '🇩🇪',
  'it': '🇮🇹',
  'pt': '🇵🇹',
  // Nordic
  'da': '🇩🇰',
  'no': '🇳🇴',
  'fi': '🇫🇮',
  'sv': '🇸🇪',
  'is': '🇮🇸',
  // Central European
  'cs': '🇨🇿',
  'sk': '🇸🇰',
  'sl': '🇸🇮',
  'pl': '🇵🇱',
  // Baltic
  'et': '🇪🇪',
  'lv': '🇱🇻',
  'lt': '🇱🇹',
  // Other European
  'el': '🇬🇷',
  'nl': '🇳🇱',
  // Asian
  'ko': '🇰🇷',
  'ja': '🇯🇵',
  'zh': '🇨🇳',
  // Other major
  'ar': '🇸🇦',
  'hi': '🇮🇳',
  'ru': '🇷🇺',
  'tr': '🇹🇷',
  'id': '🇮🇩',
  // Additional Asian
  'th': '🇹🇭',
  'vi': '🇻🇳',
  'ms': '🇲🇾',
  'ur': '🇵🇰',
  'bn': '🇧🇩',
  'fa': '🇮🇷',
  'tl': '🇵🇭',
  // Additional European
  'ro': '🇷🇴',
  'bg': '🇧🇬',
  'hr': '🇭🇷',
  'hu': '🇭🇺',
  'uk': '🇺🇦',
  'he': '🇮🇱',
  'sw': '🇰🇪'
};

// Get supported languages for a city (always includes English + local language)
export const getCitySupportedLanguages = (cityLanguage: string): CityLanguage[] => {
  const localLang = cityLanguage as CityLanguage;
  
  // Always include English
  if (localLang === 'en') {
    return ['en'];
  }
  
  // Include both English and local language
  return ['en', localLang];
};

// Check if a language is supported for cities
export const isCityLanguageSupported = (language: string): language is CityLanguage => {
  return Object.keys(cityLanguageNames).includes(language);
};

// Get the local language code from city data
export const getCityLocalLanguage = (cityLanguage: string): CityLanguage => {
  if (isCityLanguageSupported(cityLanguage)) {
    return cityLanguage;
  }
  return 'en'; // fallback to English
};

// Generate language-specific URLs for cities
export const getCityLanguageUrls = (citySlug: string, serviceSlug?: string): Array<{
  language: CityLanguage;
  url: string;
  languageName: string;
  flag: string;
}> => {
  // This would be called with the city's local language
  // For now, we'll support the main languages + local detection
  const supportedLanguages: CityLanguage[] = ['en', 'es', 'fr', 'de', 'it', 'pt'];
  
  return supportedLanguages.map(lang => ({
    language: lang,
    url: serviceSlug 
      ? `/${lang === 'en' ? '' : lang + '/'}city/${citySlug}/${serviceSlug}`
      : `/${lang === 'en' ? '' : lang + '/'}city/${citySlug}`,
    languageName: cityLanguageNames[lang],
    flag: cityLanguageFlags[lang]
  }));
};